import {
  reportErrorToSentry,
  getStatusCodeSeverity,
  setSentryRoute,
} from './sentry';
import type { AuthErrorContext } from '@/features/auth/types';

/**
 * A centralized service for reporting errors throughout the application.
 * Integrates with Sentry for production error tracking while maintaining
 * console logging for development.
 */

interface ErrorContext {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

class ErrorService {
  /**
   * Reports an error, logging it to the console and sending to Sentry with optional context.
   *
   * @param error The error object or message to report.
   * @param context Additional contextual information about the error.
   */
  public reportError(error: unknown, context?: ErrorContext): void {
    console.error('[ErrorService] Error Reported:', error);
    if (context) {
      console.error('[ErrorService] Context:', context);
    }

    // Determine severity based on context
    const statusCode = context?.statusCode as number | undefined;
    const severity = getStatusCodeSeverity(statusCode);

    // Enhanced context for Sentry
    const sentryContext = {
      ...context,
      timestamp: new Date().toISOString(),
      user_agent:
        typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
    };

    // Report to Sentry
    reportErrorToSentry(error, sentryContext, severity);
  }

  /**
   * Reports authentication-related errors with enhanced context for troubleshooting
   *
   * @param error The authentication error
   * @param authContext Authentication-specific context including attempted username
   */
  public reportAuthError(error: unknown, authContext: AuthErrorContext): void {
    console.error('[ErrorService] Auth Error Reported:', error);
    console.error('[ErrorService] Auth Context:', authContext);

    // Always treat auth errors as 'error' severity unless it's a critical failure
    const severity =
      authContext.operation === 'login' &&
      error instanceof Error &&
      error.message.includes('network')
        ? 'fatal'
        : 'error';

    const enhancedContext = {
      ...authContext,
      error_type: 'authentication',
      timestamp: new Date().toISOString(),
      url: typeof window !== 'undefined' ? window.location.href : undefined,
    };

    reportErrorToSentry(error, enhancedContext, severity);
  }

  /**
   * Reports API-related errors with request/response context
   *
   * @param error The API error
   * @param operationContext Description of the API operation
   * @param requestContext Additional request context (endpoint, payload, etc.)
   */
  public reportApiError(
    error: unknown,
    operationContext: string,
    requestContext?: {
      endpoint?: string;
      method?: string;
      statusCode?: number;
      isNetworkError?: boolean;
      requestPayload?: Record<string, unknown>;
      responseData?: Record<string, unknown>;
    }
  ): void {
    console.error('[ErrorService] API Error Reported:', error);
    console.error('[ErrorService] Operation:', operationContext);
    if (requestContext) {
      console.error('[ErrorService] Request Context:', requestContext);
    }

    const severity = requestContext?.isNetworkError
      ? 'fatal'
      : getStatusCodeSeverity(requestContext?.statusCode);

    const enhancedContext = {
      operation: operationContext,
      error_type: 'api',
      ...requestContext,
      timestamp: new Date().toISOString(),
    };

    reportErrorToSentry(error, enhancedContext, severity);
  }

  /**
   * Set the current route context for subsequent error reports.
   *
   * Updates the Sentry scope with the current route and optional search parameters
   * to provide better context for all errors reported from this route. This method
   * should be called whenever the user navigates to a new route to ensure accurate
   * error location tracking.
   *
   * @param route - Current route path (e.g., '/contracts/search', '/dashboard')
   * @param searchParams - Optional search/query parameters as key-value pairs
   *
   * @example
   * ```typescript
   * // Set route context on navigation
   * errorService.setRouteContext('/contracts/search');
   *
   * // Set route context with search parameters
   * errorService.setRouteContext('/contracts/search', {
   *   page: '1',
   *   limit: '25',
   *   query: 'toyota'
   * });
   *
   * // Use in router navigation hooks
   * useEffect(() => {
   *   const search = useSearch({ strict: false });
   *   errorService.setRouteContext(location.pathname, search);
   * }, [location.pathname]);
   * ```
   *
   * @example
   * ```typescript
   * // Integration with TanStack Router
   * import { useLocation, useSearch } from '@tanstack/react-router';
   *
   * function useRouteErrorContext() {
   *   const location = useLocation();
   *   const search = useSearch({ strict: false });
   *
   *   useEffect(() => {
   *     // Convert search params and set route context
   *     const searchParams = convertSearchParamsForSentry(search);
   *     errorService.setRouteContext(location.pathname, searchParams);
   *   }, [location.pathname, search]);
   * }
   * ```
   */
  public setRouteContext(
    route: string,
    searchParams?: Record<string, string>
  ): void {
    setSentryRoute(route, searchParams);
  }
}

/**
 * Singleton instance of the ErrorService.
 */
export const errorService = new ErrorService();
